import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ADMIN_TOUR,
  ADMIN_TOUR_VERSION,
  TOUR_VERSION,
  VENDOR_TOUR,
  tourForPath,
  type TourStep,
} from './steps'

const seenKeyFor = (version: string) => `ps:tour:${version}:seen`

interface TourContextValue {
  start: () => void
}
const TourContext = createContext<TourContextValue>({ start: () => {} })
export const useTour = () => useContext(TourContext)

interface Rect {
  top: number
  left: number
  width: number
  height: number
  radius: number
}

const readRect = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0
  return { top: r.top, left: r.left, width: r.width, height: r.height, radius }
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()

  const [active, setActive] = useState(false)
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  const [tour, setTour] = useState<{ steps: TourStep[]; version: string } | null>(null)
  const steps = tour?.steps ?? []

  const start = useCallback(() => {
    setTour(tourForPath(location.pathname))
    setIndex(0)
    setActive(true)
  }, [location.pathname])

  const finish = useCallback(() => {
    setActive(false)
    setRect(null)
    const version = tour?.version
    setTour(null)
    if (version) {
      try {
        localStorage.setItem(seenKeyFor(version), '1')
      } catch {
        /* localStorage no disponible */
      }
    }
  }, [tour])

  // Autostart del vendedor la primera vez que entra al home (/ exacto). Sin este guard de
  // ruta, el tour arrancaba también sobre /admin/conexion o /primer-ingreso (pantallas de
  // paso obligatorio) y colisionaba con sus redirects — el tour intentaba navegar a otra
  // ruta de sus propios pasos mientras el guard de esa pantalla rebotaba de vuelta.
  useEffect(() => {
    if (location.pathname !== '/' || active) return
    let seen = true
    try {
      seen = localStorage.getItem(seenKeyFor(TOUR_VERSION)) === '1'
    } catch {
      seen = true
    }
    if (seen) return
    const t = setTimeout(() => {
      if (location.pathname === '/') {
        setTour({ steps: VENDOR_TOUR, version: TOUR_VERSION })
        setIndex(0)
        setActive(true)
      }
    }, 800) // dar tiempo a que renderice la primera pantalla
    return () => clearTimeout(t)
  }, [location.pathname, active])

  // Autostart del admin la primera vez que entra al home del panel (/admin exacto).
  useEffect(() => {
    if (location.pathname !== '/admin' || active) return
    let seen = true
    try {
      seen = localStorage.getItem(seenKeyFor(ADMIN_TOUR_VERSION)) === '1'
    } catch {
      seen = true
    }
    if (seen) return
    const t = setTimeout(() => {
      if (location.pathname === '/admin') {
        setTour({ steps: ADMIN_TOUR, version: ADMIN_TOUR_VERSION })
        setIndex(0)
        setActive(true)
      }
    }, 800)
    return () => clearTimeout(t)
  }, [location.pathname, active])

  // Resolver el target del paso actual: navegar si hace falta, luego buscar el elemento.
  useEffect(() => {
    if (!active) return
    const step = steps[index]

    // Si el paso vive en otra ruta, navegamos y esperamos el re-run (cambia location).
    if (step.route && location.pathname !== step.route) {
      navigate(step.route)
      return
    }
    if (!step.target) {
      setRect(null)
      return
    }

    let cancelled = false
    let tries = 0
    setRect(null)
    const tick = () => {
      if (cancelled) return
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'center' })
        requestAnimationFrame(() => {
          if (!cancelled) setRect(readRect(el))
        })
      } else if (tries++ < 30) {
        setTimeout(tick, 100) // reintenta hasta ~3s (pantalla puede estar cargando)
      } else {
        setRect(null) // no se encontró → globo centrado
      }
    }
    tick()
    return () => {
      cancelled = true
    }
  }, [active, index, steps, navigate, location.pathname])

  // Reposicionar si cambia el tamaño de la ventana.
  useEffect(() => {
    if (!active) return
    const onResize = () => {
      const step = steps[index]
      if (!step.target) return
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (el) setRect(readRect(el))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [active, index, steps])

  const next = () => (index < steps.length - 1 ? setIndex(index + 1) : finish())
  const back = () => setIndex((i) => Math.max(0, i - 1))

  return (
    <TourContext.Provider value={{ start }}>
      {children}
      {active && tour && (
        <TourOverlay
          step={steps[index]}
          rect={rect}
          index={index}
          total={steps.length}
          onNext={next}
          onBack={back}
          onSkip={finish}
        />
      )}
    </TourContext.Provider>
  )
}

function TourOverlay({
  step,
  rect,
  index,
  total,
  onNext,
  onBack,
  onSkip,
}: {
  step: TourStep
  rect: Rect | null
  index: number
  total: number
  onNext: () => void
  onBack: () => void
  onSkip: () => void
}) {
  const isLast = index === total - 1
  const pad = 8
  const margin = 12
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const placeBelow = rect ? rect.top + rect.height / 2 < vh / 2 : true

  const cardRef = useRef<HTMLDivElement>(null)
  const [cardSize, setCardSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect()
      setCardSize({ w: r.width, h: r.height })
    }
  }, [step, rect, index])

  const targetCx = rect ? rect.left + rect.width / 2 : vw / 2
  const left = Math.max(margin, Math.min(targetCx - cardSize.w / 2, vw - cardSize.w - margin))
  let top: number
  if (rect) {
    top = placeBelow ? rect.top + rect.height + pad + 12 : rect.top - pad - 12 - cardSize.h
    top = Math.max(margin, Math.min(top, vh - cardSize.h - margin))
  } else {
    top = (vh - cardSize.h) / 2
  }

  const cardStyle: React.CSSProperties = {
    left,
    top,
    visibility: cardSize.w ? 'visible' : 'hidden',
  }

  return createPortal(
    <div className="fixed inset-0 z-[90]" role="dialog" aria-modal="true" aria-label="Guía de la app">
      {rect ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: Math.min(
              rect.radius + pad,
              (Math.min(rect.width, rect.height) + pad * 2) / 2
            ),
            boxShadow: '0 0 0 3px var(--color-accent), 0 0 0 9999px rgba(15, 23, 42, 0.62)',
            transition: 'top .25s ease, left .25s ease, width .25s ease, height .25s ease',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <div className="fixed inset-0" style={{ backgroundColor: 'rgba(15, 23, 42, 0.62)' }} />
      )}

      <div
        ref={cardRef}
        className="fixed w-[min(92vw,26rem)] rounded-2xl bg-surface p-4 shadow-pop"
        style={cardStyle}
      >
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-ink-faint">
            {index + 1} / {total}
          </span>
          <button
            type="button"
            onClick={onSkip}
            className="text-[11px] font-medium text-ink-muted hover:text-ink"
          >
            Saltar guía
          </button>
        </div>

        <h3 className="text-base font-bold text-ink">{step.title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">{step.body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            disabled={index === 0}
            className="btn-ghost px-3 py-2 text-sm disabled:opacity-0"
          >
            <ChevronLeft className="h-4 w-4" /> Atrás
          </button>
          <button type="button" onClick={onNext} className="btn-primary px-4 py-2 text-sm">
            {isLast ? '¡Entendido!' : 'Siguiente'}
            {!isLast && <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
